import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import {
  AuthTokenClaims,
  AuthUser,
  isAdmin,
  logout as logoutRequest,
  refreshToken,
  sendCode,
  userFromClaims,
  verifyCode
} from '../utils/clients/EstudiAuthClient';

interface AdminAuthContextProps {
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminUser: boolean;
  user: AuthUser | undefined;
  requestCode: (email: string) => Promise<void>;
  submitCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | undefined;
}

const AdminAuthContext = createContext<AdminAuthContextProps>({
  isLoading: true,
  isAuthenticated: false,
  isAdminUser: false,
  user: undefined,
  requestCode: () => Promise.reject(),
  submitCode: () => Promise.reject(),
  logout: () => Promise.reject(),
  getAccessToken: () => undefined
});

export const useAdminAuthContext = () => useContext(AdminAuthContext);

interface AdminAuthProviderProps {
  children: ReactNode;
}

/*
 * The access token is kept in memory only. The refresh token lives in an httpOnly cookie set by
 * the api, so a reload restores the session without ever persisting the token in the browser.
 */
const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const applyToken = useCallback((token: string) => {
    setAccessToken(token);
    setUser(userFromClaims(jwtDecode<AuthTokenClaims>(token)));
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        applyToken(await refreshToken());
      } catch (error) {
        setUser(undefined);
        setAccessToken(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    void restoreSession();
  }, [applyToken]);

  const requestCode = async (email: string): Promise<void> => sendCode(email);

  const submitCode = async (email: string, code: string): Promise<void> => {
    applyToken(await verifyCode(email, code));
  };

  const logout = async (): Promise<void> => {
    await logoutRequest();
    setUser(undefined);
    setAccessToken(undefined);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isLoading,
        isAuthenticated: !!user,
        isAdminUser: isAdmin(user),
        user,
        requestCode,
        submitCode,
        logout,
        getAccessToken: () => accessToken
      }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
